import mapboxgl from 'mapbox-gl';
import earcut from 'earcut';
import {
  getMezzanineParent,
  getStoreyElevation,
} from '@shared/helpers/design/storey.helpers';
import {
  getSelectedStoreyRenderIndex,
  resolveStoreysForThreeJsMode,
} from './storey-render-targets.helpers';
import type { Shape, Storey } from '@shared/models/design/storey.model';
import type { Polygon2D } from '@shared/models/geometry.model';
import type { Coordinate } from '@shared/models/geography.model';
import { getShapeRingCoordinates } from '../helpers/map.helpers';
import {
  resolveStoreyFloorRgb,
  resolveStoreyFloorShapes,
  resolveStoreyRoofPolygons,
} from './extrusion.helpers.helpers';
import { mapRenderingConstants } from '../map.constants';
import {
  getCutoutShapes,
  getShapeCenter,
} from '@shared/helpers/design/cutout.helpers';
import { isPointInPolygon } from '@shared/helpers/geometry/geometry.helpers';
import type { ExtrusionRenderParams } from './extrusion-render-params.helpers';
import type {
  ExtrusionVertex,
  GeometryPiece,
  StoreyRenderSlice,
  ThreeJsMode,
} from './types';

/**
 * Offset the roof slightly lower to avoid z-fighting with the floor above.
 */
const ROOF_OFFSET_METERS = 0.00001;

/**
 * Create vertices for storey extrusions in Mapbox's mercator coordinate space.
 *
 * Notes:
 * - We keep GeoJSON polygon layers for hit-testing; this layer is visual only.
 * - 3D planes are intentionally lightweight so multiple storeys stay readable.
 */
/**
 * Build extrusion vertices for floors and separate wall/roof planes for transparency.
 */
export function buildExtrusionVertices({
  storeys,
  otherBuildingStoreys = [],
  selectedStoreyId,
  origin,
  mode,
  originMercator,
  renderParams,
  otherBuildingsOpacity = 1,
}: {
  storeys: Storey[];
  /** Sibling buildings rendered as static 3D context (DEV-2576). */
  otherBuildingStoreys?: Storey[][];
  /** Multiplier for sibling extrusion alpha (click-anywhere fade). */
  otherBuildingsOpacity?: number;
  selectedStoreyId: Storey['id'] | undefined;
  origin: Coordinate;
  mode: ThreeJsMode;
  originMercator: mapboxgl.MercatorCoordinate;
  renderParams: Pick<
    ExtrusionRenderParams,
    | 'floorOpacity'
    | 'groundFloorOpacity'
    | 'roofOpacity'
    | 'wallOpacity'
    | 'roofColor'
    | 'wallColor'
    | 'undergroundWallColor'
    | 'useFloorColorOverride'
    | 'floorColor'
  >;
}): {
  vertices: Float32Array;
  vertexCount: number;
  storeySlices: StoreyRenderSlice[];
  wallVertices: Float32Array;
  wallVertexCount: number;
  roofVertices: Float32Array;
  roofVertexCount: number;
} {
  const selectedIndex = getSelectedStoreyRenderIndex(storeys, selectedStoreyId);

  // Offset floors slightly to avoid z-fighting with the roof below.
  const floorOffset =
    mapRenderingConstants.floorOffsetMeters *
    originMercator.meterInMercatorCoordinateUnits();

  const vertices: ExtrusionVertex[] = [];
  const wallVertices: ExtrusionVertex[] = [];
  const roofVertices: ExtrusionVertex[] = [];
  const storeySlices: StoreyRenderSlice[] = [];

  const storeyGroups: {
    context: Storey[];
    toRender: Storey[];
    opacityScale: number;
  }[] = [
    {
      context: storeys,
      toRender: resolveStoreysForThreeJsMode(storeys, mode, selectedIndex),
      opacityScale: 1,
    },
    ...otherBuildingStoreys.map((siblingStoreys) => ({
      context: siblingStoreys,
      toRender: resolveStoreysForThreeJsMode(
        siblingStoreys,
        mode,
        selectedIndex,
      ),
      opacityScale: otherBuildingsOpacity,
    })),
  ];

  for (const { context, toRender, opacityScale } of storeyGroups) {
    for (const storey of toRender) {
      const baseMeters = getStoreyElevation(context, storey);
      const topMeters = baseMeters + storey.height;
      const floorRgb = renderParams.useFloorColorOverride
        ? (renderParams.floorColor as [number, number, number])
        : resolveStoreyFloorRgb(context, storey);
      const floorOpacity =
        (baseMeters <= 0
          ? renderParams.groundFloorOpacity
          : renderParams.floorOpacity) * opacityScale;

      const pieces: GeometryPiece[] = [];
      let storeyCentroidX = 0;
      let storeyCentroidY = 0;
      let storeyCentroidZ = 0;
      let storeyCentroidN = 0;

      const roofStart = roofVertices.length;
      let roofCentroidX = 0;
      let roofCentroidY = 0;
      let roofCentroidZ = 0;
      let roofCentroidN = 0;

      const shapesToRender = resolveStoreyFloorShapes(context, storey);
      const cutouts = getCutoutShapes(storey);
      const roofPolygons = resolveStoreyRoofPolygons(context, storey);
      for (const roofPolygon of roofPolygons) {
        const { xy, xRel, yRel, zTopRel, holeIndices } = buildRoofRingVertices({
          rings: roofPolygon.rings,
          origin,
          topMeters: topMeters - ROOF_OFFSET_METERS,
          originMercator,
        });
        if (xy.length < 6) continue;
        const indices = earcut(
          xy,
          holeIndices.length ? holeIndices : undefined,
        );
        if (!indices.length || renderParams.roofOpacity <= 0) continue;

        // Clip roof by the storey above to avoid z-fighting and keep the plane clean.
        pushPlaneTriangles({
          vertices: roofVertices,
          indices,
          xRel,
          yRel,
          zRel: zTopRel,
          normalZ: 1,
          color: renderParams.roofColor as [number, number, number],
          opacity: renderParams.roofOpacity * opacityScale,
        });

        for (let k = 0; k < xRel.length; k++) {
          roofCentroidX += xRel[k] ?? 0;
          roofCentroidY += yRel[k] ?? 0;
          roofCentroidZ += zTopRel[k] ?? 0;
          roofCentroidN++;
        }
      }

      const roofCount = roofVertices.length - roofStart;
      if (roofCount > 0 && roofCentroidN > 0) {
        const rc: [number, number, number] = [
          roofCentroidX / roofCentroidN,
          roofCentroidY / roofCentroidN,
          roofCentroidZ / roofCentroidN,
        ];
        pieces.push({
          buffer: 'roof',
          centroid: rc,
          first: roofStart,
          count: roofCount,
        });
        storeyCentroidX += rc[0];
        storeyCentroidY += rc[1];
        storeyCentroidZ += rc[2];
        storeyCentroidN++;
      }
      const floorStart = vertices.length;
      let floorCentroidX = 0;
      let floorCentroidY = 0;
      let floorCentroidZ = 0;
      let floorCentroidN = 0;

      for (let shapeIdx = 0; shapeIdx < shapesToRender.length; shapeIdx++) {
        const shape = shapesToRender[shapeIdx];
        if (!shape?.points?.length || shape.points.length < 3) continue;

        const ring = getShapeRingCoordinates(shape, origin);
        const open = ring.length > 1 ? ring.slice(0, -1) : [];
        if (open.length < 3) continue;

        // Match wireframe-vertices: pieceSlices use index in storey.shapes, not solid-only index.
        const solidWireframeShapeIndex = storey.shapes.findIndex(
          (s) => s.id === shape.id,
        );
        const enclosedCutoutHoles = cutouts
          .filter((cutout) =>
            isPointInPolygon(getShapeCenter(cutout), shape.points),
          )
          .map((cutout) => {
            const holeRing = getShapeRingCoordinates(cutout, origin);
            const openHole = holeRing.length > 1 ? holeRing.slice(0, -1) : [];
            const wfIndex = storey.shapes.findIndex((s) => s.id === cutout.id);
            return { openHole, wfIndex };
          })
          .filter(
            (h): h is { openHole: [number, number][]; wfIndex: number } =>
              h.openHole.length >= 3 && h.wfIndex >= 0,
          );

        const holes = enclosedCutoutHoles.map((h) => h.openHole);

        const xy: number[] = [];
        const xRel: number[] = [];
        const yRel: number[] = [];
        const zBaseRel: number[] = [];
        const zTopRel: number[] = [];
        const holeIndices: number[] = [];
        let vertexOffset = 0;

        const allRings = [open, ...holes];
        for (const ringPoints of allRings) {
          if (vertexOffset > 0) holeIndices.push(vertexOffset);
          for (const [lng, lat] of ringPoints) {
            const ground = mapboxgl.MercatorCoordinate.fromLngLat(
              { lng, lat },
              0,
            );
            const base = mapboxgl.MercatorCoordinate.fromLngLat(
              { lng, lat },
              baseMeters,
            );
            const top = mapboxgl.MercatorCoordinate.fromLngLat(
              { lng, lat },
              topMeters,
            );

            const xr = ground.x - originMercator.x;
            const yr = ground.y - originMercator.y;
            xy.push(xr, yr);
            xRel.push(xr);
            yRel.push(yr);
            zBaseRel.push(base.z - originMercator.z);
            zTopRel.push(top.z - originMercator.z);
          }
          vertexOffset += ringPoints.length;
        }
        const indices = earcut(
          xy,
          holeIndices.length ? holeIndices : undefined,
        );

        pushPlaneTriangles({
          vertices,
          indices,
          xRel,
          yRel,
          zRel: zBaseRel,
          normalZ: -1,
          color: floorRgb,
          opacity: floorOpacity,
          zOffset: floorOffset,
        });

        for (let k = 0; k < open.length; k++) {
          floorCentroidX += xRel[k] ?? 0;
          floorCentroidY += yRel[k] ?? 0;
          floorCentroidZ += (zBaseRel[k] ?? 0) + floorOffset;
          floorCentroidN++;
        }

        if (storey.mezzanine) continue;

        // Walls: outer ring plus cutout hole rings; per-ring winding for correct normals.
        // wfShapeIndex must match wireframe pieceSlices (storey.shapes index) so interleaved lines draw.
        const wallRingMeta: {
          ringPoints: [number, number][];
          wfShapeIndex: number;
        }[] = [
          {
            ringPoints: open,
            wfShapeIndex:
              solidWireframeShapeIndex >= 0
                ? solidWireframeShapeIndex
                : shapeIdx,
          },
          ...enclosedCutoutHoles.map((h) => ({
            ringPoints: h.openHole,
            wfShapeIndex: h.wfIndex,
          })),
        ];

        let ringOffset = 0;
        for (const { ringPoints, wfShapeIndex } of wallRingMeta) {
          let signedArea2 = 0;
          for (let k = 0; k < ringPoints.length; k++) {
            const kk = (k + 1) % ringPoints.length;
            const ia = ringOffset + k;
            const ja = ringOffset + kk;
            signedArea2 +=
              (xRel[ia] ?? 0) * (yRel[ja] ?? 0) -
              (xRel[ja] ?? 0) * (yRel[ia] ?? 0);
          }
          const windingSign = signedArea2 >= 0 ? 1 : -1;

          for (let i = 0; i < ringPoints.length; i += 1) {
            const j = (i + 1) % ringPoints.length;
            const iAbs = ringOffset + i;
            const jAbs = ringOffset + j;

            const xi = xRel[iAbs];
            const yi = yRel[iAbs];
            const xj = xRel[jAbs];
            const yj = yRel[jAbs];
            const zbi = zBaseRel[iAbs];
            const zbj = zBaseRel[jAbs];
            const zti = zTopRel[iAbs];
            const ztj = zTopRel[jAbs];

            if (
              xi === undefined ||
              yi === undefined ||
              xj === undefined ||
              yj === undefined ||
              zbi === undefined ||
              zbj === undefined ||
              zti === undefined ||
              ztj === undefined
            ) {
              continue;
            }

            const [nx, ny] = computeWallNormal2D({ xi, yi, xj, yj });

            const edgeWallStart = wallVertices.length;
            pushWallQuads({
              vertices: wallVertices,
              xi,
              yi,
              xj,
              yj,
              zbi,
              zbj,
              zti,
              ztj,
              nx,
              ny,
              above: renderParams.wallColor as [number, number, number],
              underground: renderParams.undergroundWallColor as [
                number,
                number,
                number,
              ],
              aboveOpacity: renderParams.wallOpacity * opacityScale,
              undergroundOpacity:
                mapRenderingConstants.undergroundWallOpacity * opacityScale,
            });

            const edgeWallCount = wallVertices.length - edgeWallStart;
            const outNx = nx * windingSign;
            const outNy = ny * windingSign;
            if (edgeWallCount > 0) {
              const wc: [number, number, number] = [
                (xi + xj) / 2,
                (yi + yj) / 2,
                (zbi + zbj) / 2,
              ];
              pieces.push({
                buffer: 'wall',
                centroid: wc,
                first: edgeWallStart,
                count: edgeWallCount,
                normal: [outNx, outNy],
                shapeIndex: wfShapeIndex,
                edgeIndex: i,
              });
            }
          }
          ringOffset += ringPoints.length;
        }
      }

      const floorCount = vertices.length - floorStart;
      if (floorCount > 0 && floorCentroidN > 0) {
        const fc: [number, number, number] = [
          floorCentroidX / floorCentroidN,
          floorCentroidY / floorCentroidN,
          floorCentroidZ / floorCentroidN,
        ];
        pieces.push({
          buffer: 'floor',
          centroid: fc,
          first: floorStart,
          count: floorCount,
        });
        storeyCentroidX += fc[0];
        storeyCentroidY += fc[1];
        storeyCentroidZ += fc[2];
        storeyCentroidN++;
      }

      if (pieces.length > 0) {
        const parentStoreyId = storey.mezzanine
          ? getMezzanineParent(context, storey)?.id
          : undefined;
        storeySlices.push({
          storeyId: storey.id,
          centroid:
            storeyCentroidN > 0
              ? [
                  storeyCentroidX / storeyCentroidN,
                  storeyCentroidY / storeyCentroidN,
                  storeyCentroidZ / storeyCentroidN,
                ]
              : [0, 0, 0],
          pieces,
          isMezzanine: Boolean(storey.mezzanine),
          parentStoreyId,
        });
      }
    }
  }

  const main = packExtrusionVertices(vertices);
  const walls = packExtrusionVertices(wallVertices);
  const roof = packExtrusionVertices(roofVertices);
  return {
    vertices: main.vertices,
    vertexCount: main.vertexCount,
    storeySlices,
    wallVertices: walls.vertices,
    wallVertexCount: walls.vertexCount,
    roofVertices: roof.vertices,
    roofVertexCount: roof.vertexCount,
  };
}

/**
 * Build earcut inputs for roof polygons that may include holes.
 */
function buildRoofRingVertices({
  rings,
  origin,
  topMeters,
  originMercator,
}: {
  rings: Polygon2D[];
  origin: Coordinate;
  topMeters: number;
  originMercator: mapboxgl.MercatorCoordinate;
}): {
  xy: number[];
  xRel: number[];
  yRel: number[];
  zTopRel: number[];
  holeIndices: number[];
} {
  const xy: number[] = [];
  const xRel: number[] = [];
  const yRel: number[] = [];
  const zTopRel: number[] = [];
  const holeIndices: number[] = [];
  let vertexCount = 0;

  for (const ring of rings) {
    const ringCoordinates = getRingCoordinatesFromPolygon(ring, origin);
    const open = ringCoordinates.length > 1 ? ringCoordinates.slice(0, -1) : [];
    if (open.length < 3) continue;
    // Track where each hole starts for earcut.
    if (vertexCount > 0) holeIndices.push(vertexCount);

    for (const [lng, lat] of open) {
      const ground = mapboxgl.MercatorCoordinate.fromLngLat({ lng, lat }, 0);
      const top = mapboxgl.MercatorCoordinate.fromLngLat(
        { lng, lat },
        topMeters,
      );
      const xr = ground.x - originMercator.x;
      const yr = ground.y - originMercator.y;
      xy.push(xr, yr);
      xRel.push(xr);
      yRel.push(yr);
      zTopRel.push(top.z - originMercator.z);
    }
    vertexCount += open.length;
  }

  return { xy, xRel, yRel, zTopRel, holeIndices };
}

/**
 * Get ring coordinates for a polygon in the same way as shapes.
 */
function getRingCoordinatesFromPolygon(
  points: Polygon2D,
  origin: Coordinate,
): [number, number][] {
  const shape: Shape = { id: 'roof-ring', points };
  return getShapeRingCoordinates(shape, origin);
}

function computeWallNormal2D({
  xi,
  yi,
  xj,
  yj,
}: {
  xi: number;
  yi: number;
  xj: number;
  yj: number;
}): [number, number] {
  const dx = xj - xi;
  const dy = yj - yi;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (!len) return [1, 0];

  // A perpendicular in XY plane; sign does not matter because the shader uses abs(dot(n, l)).
  const nx = dy / len;
  const ny = -dx / len;
  return [nx, ny];
}

/**
 * Push triangles for a triangulated plane (roof or floor) using shared indices.
 * Optional zOffset lifts or lowers the plane in Mercator units to avoid z-fighting.
 */
function pushPlaneTriangles({
  vertices,
  indices,
  xRel,
  yRel,
  zRel,
  normalZ,
  color,
  opacity,
  zOffset = 0,
}: {
  vertices: ExtrusionVertex[];
  indices: number[];
  xRel: number[];
  yRel: number[];
  zRel: number[];
  normalZ: number;
  color: readonly [number, number, number];
  opacity: number;
  zOffset?: number;
}): void {
  const [r, g, b] = color;
  for (let i = 0; i < indices.length; i += 3) {
    const i0 = indices[i];
    const i1 = indices[i + 1];
    const i2 = indices[i + 2];
    if (i0 === undefined || i1 === undefined || i2 === undefined) continue;

    const x0 = xRel[i0];
    const y0 = yRel[i0];
    const z0 = zRel[i0];
    const x1 = xRel[i1];
    const y1 = yRel[i1];
    const z1 = zRel[i1];
    const x2 = xRel[i2];
    const y2 = yRel[i2];
    const z2 = zRel[i2];

    if (
      x0 === undefined ||
      y0 === undefined ||
      z0 === undefined ||
      x1 === undefined ||
      y1 === undefined ||
      z1 === undefined ||
      x2 === undefined ||
      y2 === undefined ||
      z2 === undefined
    ) {
      continue;
    }

    const z0Offset = z0 + zOffset;
    const z1Offset = z1 + zOffset;
    const z2Offset = z2 + zOffset;

    vertices.push({
      x: x0,
      y: y0,
      z: z0Offset,
      nx: 0,
      ny: 0,
      nz: normalZ,
      r,
      g,
      b,
      a: opacity,
    });
    vertices.push({
      x: x1,
      y: y1,
      z: z1Offset,
      nx: 0,
      ny: 0,
      nz: normalZ,
      r,
      g,
      b,
      a: opacity,
    });
    vertices.push({
      x: x2,
      y: y2,
      z: z2Offset,
      nx: 0,
      ny: 0,
      nz: normalZ,
      r,
      g,
      b,
      a: opacity,
    });
  }
}

/**
 * Push wall quads for a polygon edge, split at ground to keep rendering stable.
 */
function pushWallQuads({
  vertices,
  xi,
  yi,
  xj,
  yj,
  zbi,
  zbj,
  zti,
  ztj,
  nx,
  ny,
  above,
  underground,
  aboveOpacity,
  undergroundOpacity,
}: {
  vertices: ExtrusionVertex[];
  xi: number;
  yi: number;
  xj: number;
  yj: number;
  zbi: number;
  zbj: number;
  zti: number;
  ztj: number;
  nx: number;
  ny: number;
  above: readonly [number, number, number];
  underground: readonly [number, number, number];
  aboveOpacity: number;
  undergroundOpacity: number;
}): void {
  const zCut = 0;

  const isFullyUnderground = zti <= zCut && ztj <= zCut;
  const isFullyAbove = zbi >= zCut && zbj >= zCut;

  if (isFullyUnderground) {
    pushWallQuad(vertices, {
      xi,
      yi,
      xj,
      yj,
      z0i: zbi,
      z0j: zbj,
      z1i: zti,
      z1j: ztj,
      nx,
      ny,
      color: underground,
      opacity: undergroundOpacity,
    });
    return;
  }
  if (isFullyAbove) {
    pushWallQuad(vertices, {
      xi,
      yi,
      xj,
      yj,
      z0i: zbi,
      z0j: zbj,
      z1i: zti,
      z1j: ztj,
      nx,
      ny,
      color: above,
      opacity: aboveOpacity,
    });
    return;
  }

  // Split at z=0 (ground) into two quads.
  const z0iBelow = Math.min(zbi, zCut);
  const z0jBelow = Math.min(zbj, zCut);
  const z1iBelow = Math.min(zti, zCut);
  const z1jBelow = Math.min(ztj, zCut);
  if (z0iBelow !== z1iBelow || z0jBelow !== z1jBelow) {
    pushWallQuad(vertices, {
      xi,
      yi,
      xj,
      yj,
      z0i: z0iBelow,
      z0j: z0jBelow,
      z1i: z1iBelow,
      z1j: z1jBelow,
      nx,
      ny,
      color: underground,
      opacity: undergroundOpacity,
    });
  }

  const z0iAbove = Math.max(zbi, zCut);
  const z0jAbove = Math.max(zbj, zCut);
  const z1iAbove = Math.max(zti, zCut);
  const z1jAbove = Math.max(ztj, zCut);
  if (z0iAbove !== z1iAbove || z0jAbove !== z1jAbove) {
    pushWallQuad(vertices, {
      xi,
      yi,
      xj,
      yj,
      z0i: z0iAbove,
      z0j: z0jAbove,
      z1i: z1iAbove,
      z1j: z1jAbove,
      nx,
      ny,
      color: above,
      opacity: aboveOpacity,
    });
  }
}

function pushWallQuad(
  vertices: ExtrusionVertex[],
  {
    xi,
    yi,
    xj,
    yj,
    z0i,
    z0j,
    z1i,
    z1j,
    nx,
    ny,
    color,
    opacity,
  }: {
    xi: number;
    yi: number;
    xj: number;
    yj: number;
    z0i: number;
    z0j: number;
    z1i: number;
    z1j: number;
    nx: number;
    ny: number;
    color: readonly [number, number, number];
    opacity: number;
  },
): void {
  const [r, g, b] = color;

  // Tri 1
  vertices.push({ x: xi, y: yi, z: z0i, nx, ny, nz: 0, r, g, b, a: opacity });
  vertices.push({ x: xj, y: yj, z: z0j, nx, ny, nz: 0, r, g, b, a: opacity });
  vertices.push({ x: xj, y: yj, z: z1j, nx, ny, nz: 0, r, g, b, a: opacity });

  // Tri 2
  vertices.push({ x: xi, y: yi, z: z0i, nx, ny, nz: 0, r, g, b, a: opacity });
  vertices.push({ x: xj, y: yj, z: z1j, nx, ny, nz: 0, r, g, b, a: opacity });
  vertices.push({ x: xi, y: yi, z: z1i, nx, ny, nz: 0, r, g, b, a: opacity });
}

function packExtrusionVertices(vertices: ExtrusionVertex[]): {
  vertices: Float32Array;
  vertexCount: number;
} {
  const vertexCount = vertices.length;
  if (vertexCount <= 0) return { vertices: new Float32Array(), vertexCount: 0 };

  // Layout: pos(3), normal(3), color(4) = 10 floats
  const out = new Float32Array(vertexCount * 10);

  for (let v = 0; v < vertexCount; v += 1) {
    const data = vertices[v];
    if (!data) continue;

    const o = v * 10;
    out[o] = data.x;
    out[o + 1] = data.y;
    out[o + 2] = data.z;
    out[o + 3] = data.nx;
    out[o + 4] = data.ny;
    out[o + 5] = data.nz;
    out[o + 6] = data.r;
    out[o + 7] = data.g;
    out[o + 8] = data.b;
    out[o + 9] = data.a;
  }

  return { vertices: out, vertexCount };
}
